import React, { useState } from "react";
import { FaTimes, FaDownload, FaCheckSquare, FaRegSquare, FaSpinner, FaFileArchive } from "react-icons/fa";

const EXPORT_CATEGORIES = [
  { key: "profile",       label: "Profile",             desc: "User account & service details" },
  { key: "chats",         label: "Chat History",         desc: "All messages with admin" },
  { key: "documents",     label: "Documents",            desc: "Uploaded files & RA documents" },
  { key: "folders",       label: "Folders",              desc: "Folder structure" },
  { key: "invoices",      label: "Invoices",             desc: "Invoice files & report" },
  { key: "payments",      label: "Payments",             desc: "Payment & transaction records" },
  { key: "workflow",      label: "Workflow / Stepcards", desc: "Steps, history & checklists" },
  { key: "logs",          label: "Activity Logs",        desc: "Login history & activity" },
  { key: "notifications", label: "Notifications",        desc: "All notifications" },
  { key: "tickets",       label: "Support Tickets",      desc: "Support & query history" },
  { key: "reports",       label: "Reports",              desc: "Bookkeeping, annual & meetings" },
  { key: "attachments",   label: "Chat Attachments",     desc: "Files sent in chat" },
];

const ExportUserModal = ({ isOpen, onClose, user, isAllExport }) => {
  const [options, setOptions] = useState(
    EXPORT_CATEGORIES.reduce((acc, c) => ({ ...acc, [c.key]: true }), {})
  );
  const [loading, setLoading]   = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [progress, setProgress]  = useState(0); // 0-100
  const [error, setError]        = useState("");

  if (!isOpen) return null;

  const selected    = EXPORT_CATEGORIES.filter(c => options[c.key]).length;
  const allSelected = selected === EXPORT_CATEGORIES.length;
  const noneSelected = selected === 0;

  const toggleAll = () => {
    const newVal = !allSelected;
    setOptions(EXPORT_CATEGORIES.reduce((acc, c) => ({ ...acc, [c.key]: newVal }), {}));
  };

  const toggle = (key) => setOptions(prev => ({ ...prev, [key]: !prev[key] }));

  const handleClose = () => {
    if (loading) return;
    onClose();
    setError("");
    setStatusMsg("");
    setProgress(0);
  };

  const handleDownload = async () => {
    if (noneSelected) {
      setError("Please select at least one category.");
      return;
    }

    setLoading(true);
    setError("");
    setProgress(10);
    setStatusMsg(
      isAllExport
        ? "Collecting all platform data… This may take several minutes."
        : `Collecting data for ${user?.name || "user"}…`
    );

    try {
      const token    = localStorage.getItem("adminToken");
      const endpoint = isAllExport
        ? `${process.env.REACT_APP_API_BASE_URL}/api/admin/export-all`
        : `${process.env.REACT_APP_API_BASE_URL}/api/admin/export-user`;

      const body = isAllExport
        ? { exportOptions: options }
        : { userId: user._id, exportOptions: options };

      setProgress(20);

      const response = await fetch(endpoint, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      setProgress(50);

      // Try to parse JSON error if response is not OK
      if (!response.ok) {
        let errMsg = `Server returned ${response.status}`;
        try {
          const contentType = response.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            const errData = await response.json();
            errMsg = errData.message || errMsg;
          } else {
            errMsg = await response.text() || errMsg;
          }
        } catch (_) { /* ignore parse error */ }
        throw new Error(errMsg);
      }

      // Confirm we got a ZIP back
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("zip") && !contentType.includes("octet-stream")) {
        // Might be a JSON error body that slipped through
        try {
          const errData = await response.json();
          throw new Error(errData.message || "Unexpected response from server");
        } catch (_) { /* not json */ }
      }

      setStatusMsg("Downloading file…");
      setProgress(75);

      const blob = await response.blob();

      setProgress(95);

      // Construct a sensible filename
      const safeName = (isAllExport ? "platform" : (user?.name || "user")).replace(/[^a-zA-Z0-9]/g, "_");
      const date     = new Date().toISOString().slice(0, 10);
      const filename = isAllExport
        ? `platform-export-${date}.zip`
        : `user-export-${safeName}-${date}.zip`;

      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href     = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setProgress(100);
      setStatusMsg("✅ Download complete!");

      setTimeout(() => {
        handleClose();
      }, 1800);

    } catch (err) {
      console.error("Export error:", err);
      setError(err.message || "Export failed. Please try again.");
      setStatusMsg("");
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-xl mx-4 bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 bg-gradient-to-r from-indigo-900/60 to-purple-900/60">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500/20 rounded-lg">
              <FaFileArchive className="text-indigo-400" size={18} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white">
                {isAllExport ? "Export All Platform Data" : "Export User Data"}
              </h2>
              {!isAllExport && user && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {user.name} — {user.email}
                </p>
              )}
              {isAllExport && (
                <p className="text-xs text-gray-400 mt-0.5">
                  Exports data for every user on the platform
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors disabled:opacity-40"
          >
            <FaTimes size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">

          {/* Select All */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-300">
              Select categories  
              <span className="ml-2 text-xs text-gray-500">({selected}/{EXPORT_CATEGORIES.length} selected)</span>
            </span>
            <button
              onClick={toggleAll}
              disabled={loading}
              className="text-xs font-medium px-3 py-1.5 rounded-lg bg-indigo-600/30 text-indigo-300 hover:bg-indigo-600/50 border border-indigo-600/30 transition-colors disabled:opacity-40"
            >
              {allSelected ? "Deselect All" : "Select All"}
            </button>
          </div>

          {/* Checkboxes */}
          <div className="grid grid-cols-2 gap-2 mb-5">
            {EXPORT_CATEGORIES.map((cat) => (
              <button
                key={cat.key}
                onClick={() => toggle(cat.key)}
                disabled={loading}
                title={cat.desc}
                className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border text-left text-sm transition-all duration-150 disabled:opacity-40 ${
                  options[cat.key]
                    ? "bg-indigo-600/20 border-indigo-500/60 text-white"
                    : "bg-gray-800/60 border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-300"
                }`}
              >
                <div className="mt-0.5 flex-shrink-0">
                  {options[cat.key]
                    ? <FaCheckSquare className="text-indigo-400" size={14} />
                    : <FaRegSquare className="text-gray-600" size={14} />}
                </div>
                <div>
                  <div className="font-medium">{cat.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{cat.desc}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Progress bar */}
          {(loading || progress > 0) && (
            <div className="mb-4">
              <div className="h-1.5 w-full bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Status */}
          {statusMsg && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-indigo-900/30 border border-indigo-700/40 text-indigo-300 text-sm flex items-center gap-2">
              {loading && <FaSpinner className="animate-spin flex-shrink-0" size={12} />}
              {statusMsg}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-red-900/30 border border-red-700/40 text-red-300 text-sm">
              ⚠️ {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-700 bg-gray-900/60">
          <p className="text-xs text-gray-500">
            Output: ZIP with PDF/Excel + JSON files
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleDownload}
              disabled={loading || noneSelected}
              className="px-5 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 transition-colors disabled:opacity-40 flex items-center gap-2"
            >
              {loading
                ? <><FaSpinner className="animate-spin" size={13} /> Exporting…</>
                : <><FaDownload size={13} /> Download ZIP</>}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ExportUserModal;
